import type { Identifier, XYCoord } from 'dnd-core';
import type { FC, Ref } from 'react';
import { useEffect, useRef } from 'react';
import { DragSourceMonitor, useDrag, useDragLayer, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { createPortal } from 'react-dom';

export const ItemTypes = {
  CARD: 'card',
};

const style = {
  border: '1px dashed gray',
  padding: '0.5rem 1rem',
  marginBottom: '.5rem',
  backgroundColor: 'white',
  cursor: 'move',
};

export interface IPropsDraggableItem {
  id: string | number;
  index: number;
  // function form: attach the given ref to a handle so only the handle starts a drag (rest of the row stays interactive)
  children?: React.ReactElement | ((dragHandleRef: Ref<HTMLElement>) => React.ReactElement);
  onDragItem: (dragIndex: number, hoverIndex: number) => void;
  canDrag?: boolean;
  // style?: React.CSSProperties;
  className?: string;
}

interface IPropsDragItem {
  index: number;
  id: string | number;
  width: number;
  height: number;
  handleDx: number;
  handleDy: number;
  color?: string;
  fontFamily?: string;
}

interface IPropsDragGhost {
  item: IPropsDragItem;
  className?: string;
  children: React.ReactElement;
}

// Chrome's own drag snapshot of the row was oversized and picked up neighbouring rows, so draw a fixed-size ghost instead
const DragGhost: FC<IPropsDragGhost> = ({ item, className, children }) => {
  const offset = useDragLayer((monitor) => monitor.getSourceClientOffset());
  if (!offset) return null;
  return createPortal(
    <div
      className={className}
      style={{
        ...style,
        position: 'fixed',
        zIndex: 9999,
        pointerEvents: 'none',
        top: 0,
        left: 0,
        width: item.width,
        height: item.height,
        color: item.color,
        fontFamily: item.fontFamily,
        transform: `translate(${offset.x - item.handleDx}px, ${offset.y - item.handleDy}px)`,
        boxShadow: '0 4px 12px rgba(0,0,0,.2)',
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

const DraggableItem: FC<IPropsDraggableItem> = ({ id, children, index, canDrag = true, className, onDragItem }) => {
  const ref = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLElement>(null);
  const useHandle = typeof children === 'function';
  const [{ handlerId }, drop] = useDrop<IPropsDragItem, void, { handlerId: Identifier | null }>({
    accept: ItemTypes.CARD,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: IPropsDragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      onDragItem(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging, dragItem }, drag, preview] = useDrag({
    type: ItemTypes.CARD,
    item: () => {
      const rect = ref.current?.getBoundingClientRect();
      const handleRect = handleRef.current?.getBoundingClientRect();
      const computed = ref.current ? getComputedStyle(ref.current) : undefined;
      return {
        id,
        index,
        width: rect?.width ?? 0,
        height: rect?.height ?? 0,
        // react-dnd reports the handle's position, the ghost is drawn from the row's top-left
        handleDx: rect && handleRect ? handleRect.left - rect.left : 0,
        handleDy: rect && handleRect ? handleRect.top - rect.top : 0,
        // the ghost is portalled to <body>, so copy text styles it would otherwise inherit from the page
        color: computed?.color,
        fontFamily: computed?.fontFamily,
      };
    },
    collect: (monitor: DragSourceMonitor<IPropsDragItem, unknown>) => ({
      isDragging: monitor.isDragging(),
      dragItem: monitor.getItem(),
    }),
    canDrag,
  });

  // keep a faded placeholder so the row's slot stays visible while dragging
  const opacity = isDragging ? 0.4 : 1;

  useEffect(() => {
    if (useHandle) preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview, useHandle]);

  if (useHandle) {
    drop(ref);
    drag(handleRef);
  } else {
    drag(drop(ref));
  }

  return (
    <div ref={ref} style={{ ...style, opacity, cursor: useHandle ? 'default' : style.cursor }} data-handler-id={handlerId} className={className}>
      {typeof children === 'function' ? children(handleRef) : children}
      {useHandle && isDragging && dragItem && (
        <DragGhost item={dragItem} className={className}>
          {(children as (ref: Ref<HTMLElement>) => React.ReactElement)(() => undefined)}
        </DragGhost>
      )}
    </div>
  );
};

export default DraggableItem;
